/**
 * Dashboard Stats Cards
 * Tacit Dark Professional - Stat cards with subtle glow effects
 */

import { AlertCircle, Calendar, Clock, Users } from "lucide-react";
import type { DashboardFilter } from "@/lib/crm/dashboard";
import { cn } from "@/lib/utils";

interface StatsCardProps {
  title: string;
  count: number;
  icon: React.ComponentType<{ className?: string }>;
  color: "red" | "orange" | "indigo" | "gray";
  filter: Exclude<DashboardFilter, null>;
  selected: boolean;
  onSelect: (filter: Exclude<DashboardFilter, null>) => void;
}

function StatsCard({
  title,
  count,
  icon: Icon,
  color,
  filter,
  selected,
  onSelect,
}: StatsCardProps) {
  const colorClasses = {
    red: "border-l-status-danger",
    orange: "border-l-status-warning",
    indigo: "border-l-status-info",
    gray: "border-l-border-strong",
  };

  const iconColorClasses = {
    red: "text-status-danger",
    orange: "text-status-warning",
    indigo: "text-status-info",
    gray: "text-text-tertiary",
  };

  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={`Filter contacts by ${title}: ${count}`}
      className={cn(
        "interactive relative w-full overflow-hidden rounded-xl border border-border border-l-4 bg-card p-6 text-left shadow-elevation-1 transition-shadow",
        colorClasses[color],
        "hover:border-border-strong hover:shadow-elevation-2 active:scale-[0.99]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        selected && "ring-2 ring-primary ring-offset-2 ring-offset-background"
      )}
      onClick={() => onSelect(filter)}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="mb-1 text-sm font-medium text-muted-foreground">
            {title}
          </p>
          <p className="text-4xl font-mono font-bold text-foreground">
            {count}
          </p>
        </div>
        <div className={cn("rounded-lg bg-surface-elevated p-3", iconColorClasses[color])}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
    </button>
  );
}

interface DashboardStatsProps {
  overdueCount: number;
  dueTodayCount: number;
  dueThisWeekCount: number;
  totalActiveCount: number;
  activeFilter: DashboardFilter;
  onFilterChange: (filter: Exclude<DashboardFilter, null>) => void;
}

export function DashboardStats({
  overdueCount,
  dueTodayCount,
  dueThisWeekCount,
  totalActiveCount,
  activeFilter,
  onFilterChange,
}: DashboardStatsProps) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <StatsCard
        title="Overdue"
        count={overdueCount}
        icon={AlertCircle}
        color="red"
        filter="overdue"
        selected={activeFilter === "overdue"}
        onSelect={onFilterChange}
      />
      <StatsCard
        title="Due Today"
        count={dueTodayCount}
        icon={Clock}
        color="orange"
        filter="due-today"
        selected={activeFilter === "due-today"}
        onSelect={onFilterChange}
      />
      <StatsCard
        title="Due This Week"
        count={dueThisWeekCount}
        icon={Calendar}
        color="indigo"
        filter="due-this-week"
        selected={activeFilter === "due-this-week"}
        onSelect={onFilterChange}
      />
      <StatsCard
        title="Total Active"
        count={totalActiveCount}
        icon={Users}
        color="gray"
        filter="total-active"
        selected={activeFilter === "total-active"}
        onSelect={onFilterChange}
      />
    </div>
  );
}
